// src/pages/Automations.jsx
// Console → Automations (3.28.0, Super Admins): everything that runs on its
// own — what it does, where it runs, what it needs — with when it last ran,
// its switches and what's queued. The list comes from the backend
// (lib/automations.js), which is updated whenever an automation changes.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE } from '../firebase/config';
import { formatDate } from '../utils/format';
import { SECTION_TITLE, SECTION_SUB, PILL, PILL_ON } from '../theme/tabs';
import { useNotify } from '../components/Notifications';
import Loader from '../components/Loader';
import Emo from '../components/Emo';

const WHERE = {
  cloud: { label: 'Google Cloud', icon: '☁️', about: 'Runs in Google Cloud whether or not anyone is signed in.' },
  'apps-script': { label: 'Google Apps Script', icon: '📧', about: 'Runs in the Gmail accounts’ Apps Script projects (Triggers page).' },
  events: { label: 'On events', icon: '⚡', about: 'Runs in the backend when something happens, not on a clock.' },
  browser: { label: 'In the browser', icon: '🖥️', about: 'Runs only while someone has the portal open.' },
  mac: { label: 'Development Mac', icon: '💻', about: 'Not part of the live service.' },
};
const ORDER = ['cloud', 'apps-script', 'events', 'browser', 'mac'];

function ago(iso) {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}
// The useful parts of a last run, in words
function lastText(a) {
  const l = a.last;
  if (!l) return null;
  const bits = [];
  if (l.retriesOff) bits.push('retries are switched off');
  if (l.retriesSent) bits.push(`${l.retriesSent} retr${l.retriesSent === 1 ? 'y' : 'ies'} sent`);
  if (l.waitingSent) bits.push(`${l.waitingSent} waiting fax${l.waitingSent === 1 ? '' : 'es'} sent`);
  if (l.lookups) bits.push(`${l.lookups} number${l.lookups === 1 ? '' : 's'} looked up`);
  if (l.checked != null) bits.push(`${l.checked} failed receive${l.checked === 1 ? '' : 's'} checked`);
  if (l.added) bits.push(`${l.added} added`);
  if (l.corrected) bits.push(`${l.corrected} corrected`);
  if (l.retentionDeleted) bits.push(`${l.retentionDeleted} old record${l.retentionDeleted === 1 ? '' : 's'} deleted`);
  if (l.retentionNote) bits.push(`retention: ${l.retentionNote}`);
  if (l.testStarted) bits.push('weekday test fax started');   // 3.41.0: Health
  if (l.problems) bits.push(`${l.problems} health problem${l.problems === 1 ? '' : 's'} open`);
  if (l.emailsSent) bits.push(`${l.emailsSent} alert email${l.emailsSent === 1 ? '' : 's'} sent`);
  if (l.error) bits.push(`error: ${l.error}`);
  return bits.length ? bits.join(' · ') : 'nothing to do';
}

export default function Automations() {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/portal/control/automations`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't load the automations.");
      setData(j); setError('');
    } catch (e) { setError(e.message); }
  }, [getToken]);
  useEffect(() => { load(); }, [load]);

  if (!data) return error ? <div style={S.error}>{error}</div> : <Loader />;
  const q = data.queues;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={SECTION_TITLE}>What runs on its own</h3>
          <p style={SECTION_SUB}>Every automation of the fax service: what it does, where it runs and what it needs. Last runs are reported by the jobs themselves. This list is kept up to date with every release that adds or removes an automation.</p>
        </div>
        <button onClick={load} style={S.refresh}><Emo e="↻" /> Refresh</button>
      </div>
      {error && <div style={S.error}>{error}</div>}

      {/* 3.60.1 (owner): the Gmail card moved to Console → Email Settings → Email Rules */}
      <div style={{ ...S.meta, marginTop: 12 }}><Emo e="📮" /> Fax by email and the Gmail clean-up switch are in <a href="/portal/console/email-rules" style={{ color: 'var(--t-00897b)', fontWeight: 600 }}>Console → Email Settings → Email Rules</a>, with the log of what the clean-up deleted.</div>

      <div style={S.queueRow}>
        {[['Waiting for a busy line', q.waitingForLine], ['Scheduled (Compose)', q.scheduled], ['Retries queued', q.retriesQueued], ['Email faxes held (no account)', q.heldEmailFaxes]].map(([label, n]) => (
          <div key={label} style={S.queueCard}><div style={S.queueN}>{n ?? '—'}</div><div style={S.queueL}>{label}</div></div>
        ))}
      </div>

      {ORDER.filter(w => data.catalog.some(a => a.where === w)).map(w => (
        <section key={w} style={{ marginTop: 22 }}>
          <h4 style={S.groupTitle}><Emo e={WHERE[w].icon} /> {WHERE[w].label} <span style={S.groupAbout}>{WHERE[w].about}</span></h4>
          {data.catalog.filter(a => a.where === w).map(a => (
            <div key={a.id} style={{ ...S.card, ...(a.late ? S.cardLate : {}) }}>
              <div style={S.cardHead}>
                <div>
                  <div style={S.name}>{a.name}</div>
                  <div style={S.schedule}><Emo e="🕒" /> {a.schedule}</div>
                </div>
                <div style={{ textAlign: 'right', minWidth: 200 }}>
                  {a.runKey ? (a.last
                    ? <><div style={{ ...S.last, color: a.late ? 'var(--t-c62828)' : 'var(--t-2e7d32)' }}>{a.late ? <><Emo e="⚠️" /> Late: </> : '✓ '}{a.lastRunNote ? `${a.lastRunNote}: ` : 'Last ran '}{ago(a.last.at)}</div>
                        <div style={S.lastDetail} title={formatDate(a.last.at)}>{lastText(a)}</div></>
                    : <div style={S.lastDetail}>No run recorded yet</div>) : null}
                  {a.switches.map(s => (
                    <div key={s.key} style={{ ...S.switch, color: s.on ? 'var(--t-2e7d32)' : 'var(--t-c62828)' }} title={s.where}>{s.label}: {s.on ? 'ON' : 'OFF'}</div>
                  ))}
                </div>
              </div>
              <ul style={S.steps}>{a.steps.map(t => <li key={t}>{t}</li>)}</ul>
              <div style={S.meta}><strong>Runs by:</strong> {a.trigger}</div>
              <div style={S.meta}><strong>Needs:</strong> {a.needs.join(' · ')}</div>
              {a.note && <div style={{ ...S.meta, fontStyle: 'italic' }}>{a.note}</div>}
            </div>
          ))}
        </section>
      ))}
      <p style={{ ...S.sub, marginTop: 18 }}>Checked {formatDate(data.checkedAt)}.</p>
    </div>
  );
}

// Gmail switches (3.58.0, owner); shown in Console → Email Settings → Email
// Rules since 3.60.1 (owner). 3.58.1 (owner, 2026-10-14): the old Apps
// Scripts are archived, so fax by email only shows that it runs on the Gmail
// API, the clean-up is Live or Paused (counts only), and the one-off catch-up
// (done Oct 14) only shows its result.
export function GmailSwitches() {
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const [g, setG] = useState(null);
  const [busy, setBusy] = useState(false);
  const call = useCallback(async (method = 'GET', body) => {
    const r = await fetch(`${API_BASE}/portal/control/gmail`, { method, headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, ...(body ? { body: JSON.stringify(body) } : {}) });
    const j = await r.json(); if (!r.ok) throw new Error(j.error || "That didn't work"); return j;
  }, [getToken]);
  useEffect(() => { call().then(setG).catch(() => setG(null)); }, [call]);
  if (!g) return null;
  const setPurge = async v => {
    // 3.60.8 (owner): back to live after a pause asks whether to go back to the paused time too
    const paused = v === 'live' && g.purgePausedAt;
    const msg = v === 'live'
      ? { title: 'Switch the Gmail clean-up back to live?', message: `From its next run (every 6 hours) it permanently deletes fax emails in admin@, by the rules.${paused ? `\n\nIt was paused on ${formatDate(g.purgePausedAt)}.` : ''}\n\nThe faxes themselves stay in the portal.`, confirmLabel: 'Go live', cancelLabel: paused ? 'Keep paused' : 'Cancel', danger: true,
        ...(paused ? { choices: [
          { value: 'recent', label: 'Only from now on (the last 2 days)', hint: 'Emails from the paused time stay in admin@.' },
          { value: 'back', label: 'Also delete the emails from the paused time', hint: `Goes back to ${formatDate(g.purgePausedAt)}. A large number is spread over a few runs.` },
        ] } : {}) }
      : { title: 'Pause the Gmail clean-up?', message: 'It only counts what it would delete; nothing is deleted until you switch it back to Live. Fax emails stay in admin@ meanwhile.', confirmLabel: 'Pause' };
    const ok = await confirm(msg);
    if (!ok) return;
    setBusy(true);
    try { setG(await call('PUT', { purge: v, ...(paused ? { catchUp: ok === 'back' } : {}) })); notify({ tone: 'success', title: v === 'live' ? (ok === 'back' ? 'Clean-up is live, and it will clear the paused time too' : 'Clean-up is live') : 'Clean-up paused' }); }
    catch (e) { notify({ tone: 'error', title: "That didn't work", detail: e.message }); }
    setBusy(false);
  };
  const live = g.gateway === 'live';
  const r = g.purge === 'live' ? g.purgeLive : g.purgeWatch;
  const cu = g.catchUp;
  return (
    <div style={{ ...S.card, marginTop: 14, borderColor: 'var(--c-b2dfdb)' }}>
      <div style={S.name}><Emo e="📮" /> Email rules (Gmail)</div>
      <div style={{ ...S.switchRow, marginTop: 8 }}>
        <strong style={S.switchLabel}>Fax by email</strong>
        <span style={{ fontSize: 13, fontWeight: 700, color: live ? 'var(--t-2e7d32)' : 'var(--t-c62828)' }}>
          {live ? `✓ Gmail API, live since ${formatDate(g.liveAt)}` : `⚠️ Not live (${g.gateway}): fax by email isn't running. Contact the developer.`}
        </span>
      </div>
      <div style={{ ...S.switchRow, marginTop: 10 }}>
        <strong style={S.switchLabel}>Gmail clean-up</strong>
        <div style={S.pills}>
          {[['live', 'Live (deletes, every 6 hours)'], ['watch', 'Paused (counts only)']].map(([v, l]) => (
            <button key={v} onClick={() => v !== g.purge && setPurge(v)} disabled={busy} aria-pressed={v === g.purge} style={{ ...PILL, ...(v === g.purge ? PILL_ON : {}), fontSize: 12 }}>{l}</button>
          ))}
        </div>
      </div>
      {/* 3.60.4 (owner): which clients the clean-up covers, ticks in a drop-down */}
      <CleanupClients g={g} busy={busy} save={async (skip, catchUp = []) => {
        setBusy(true);
        try { setG(await call('PUT', { purgeSkip: skip, catchUp })); notify({ tone: 'success', title: catchUp.length ? 'Clean-up clients saved; their unticked time will be cleared too' : 'Clean-up clients saved' }); }
        catch (e) { notify({ tone: 'error', title: "Couldn't save that", detail: e.message }); }
        setBusy(false);
      }} />
      {r && <div style={S.meta}>Last run {formatDate(r.at)}: {r.threads} thread{r.threads === 1 ? '' : 's'} {g.purge === 'live' ? `found, ${r.deleted} deleted` : 'would be deleted'}{r.clients?.length ? ` (${r.clients.map(c => `${c.name} ${c.threads}`).join(' · ')})` : ''}</div>}
      {cu?.status === 'done' && <div style={S.meta}>One-off catch-up done {formatDate(cu.finishedAt)}: {cu.deleted} of {cu.threads} older threads deleted.</div>}
      <div style={{ ...S.meta, marginTop: 8 }}>The old Apps Scripts were archived on Oct 14, 2026{g.changedAt ? ` · last change ${formatDate(g.changedAt)} by ${g.changedBy}` : ''}.</div>
    </div>
  );
}

// The clients the Gmail clean-up covers (3.60.4): a drop-down of ticks, each
// saved at once; unticked = the clean-up leaves that client's emails alone
function CleanupClients({ g, busy, save }) {
  const { confirm } = useNotify();
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = e => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  const clients = g.clients || [], skip = new Set(g.purgeSkip || []);
  // 3.60.8 (owner): unticking and ticking again both ask first; ticking again
  // asks whether to go back to the time it was unticked
  const toggle = async (c, on) => {
    setOpen(false);
    if (!on) {
      if (await confirm({ title: `Stop cleaning up ${c.name}?`, message: `The clean-up will leave ${c.name}’s fax emails in admin@ until you tick it again.`, confirmLabel: `Stop cleaning up ${c.name}`, cancelLabel: 'Keep cleaning up' })) save([...skip, c.id]);
      return;
    }
    const since = g.purgeSkipSince?.[c.id];
    const rest = [...skip].filter(x => x !== c.id);
    if (!since) { save(rest); return; }
    const ok = await confirm({ title: `Clean up ${c.name} again?`, message: `${c.name} was unticked on ${formatDate(since)}. Its fax emails from then on are still in admin@.`, confirmLabel: `Clean up ${c.name}`, cancelLabel: 'Keep unticked', danger: true,
      choices: [
        { value: 'recent', label: 'Only from now on (the last 2 days)', hint: 'Emails from the unticked time stay in admin@.' },
        { value: 'back', label: `Also delete ${c.name}’s emails from the unticked time`, hint: `Goes back to ${formatDate(since)}. A large number is spread over a few runs.` },
      ] });
    if (ok) save(rest, ok === 'back' ? [c.id] : []);
  };
  if (!clients.length) return null;
  const on = clients.filter(c => !skip.has(c.id));
  const summary = on.length === clients.length ? `All ${clients.length} clients` : on.length === 0 ? 'No clients' : on.map(c => c.name).join(', ');
  return (
    <div ref={box} style={{ ...S.switchRow, marginTop: 10, position: 'relative' }}>
      <strong style={S.switchLabel}>Clients cleaned up</strong>
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} disabled={busy}
        style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', minWidth: 200, textAlign: 'left', display: 'inline-flex', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>{summary}</span><span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div role="listbox" aria-multiselectable="true" style={{ position: 'absolute', top: '100%', left: 132, zIndex: 20, marginTop: 4, background: 'var(--c-ffffff)', border: '1px solid var(--c-cfd8dc)', borderRadius: 10, boxShadow: '0 8px 24px rgb(var(--c-000000-rgb) / 0.15)', padding: 6, minWidth: 240 }}>
          {clients.map(c => (
            <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 13, color: 'var(--t-37474f)', cursor: 'pointer', borderRadius: 6 }}>
              <input type="checkbox" checked={!skip.has(c.id)} disabled={busy}
                onChange={e => toggle(c, e.target.checked)} />
              {c.name}
            </label>
          ))}
          <div style={{ fontSize: 11.5, color: 'var(--t-78909c)', padding: '4px 8px 2px' }}>Unticked: the clean-up leaves that client’s fax emails in admin@. Our test emails (Rule 4) are always cleaned.</div>
        </div>
      )}
    </div>
  );
}

const S = {
  switchRow: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  switchLabel: { fontSize: 13, color: 'var(--t-37474f)', minWidth: 120 },
  pills: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  sub: { color: 'var(--t-78909c)', fontSize: 13 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, fontSize: 13, margin: '10px 0' },
  refresh: { padding: '8px 16px', background: 'var(--c-37474f)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  queueRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginTop: 14 },
  queueCard: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 10, padding: '10px 14px' },
  queueN: { fontSize: 22, fontWeight: 700, color: 'var(--t-00695c)' },
  queueL: { fontSize: 12, color: 'var(--t-78909c)' },
  groupTitle: { margin: '0 0 8px', color: 'var(--t-37474f)', fontSize: 15 },
  groupAbout: { fontWeight: 400, color: 'var(--t-90a4ae)', fontSize: 12, marginLeft: 6 },
  card: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '14px 16px', marginBottom: 10, boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.04)' },
  cardLate: { border: '1.5px solid var(--c-ef9a9a)', background: 'var(--c-fffafa)' },
  cardHead: { display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  name: { fontSize: 15, fontWeight: 700, color: 'var(--t-00695c)' },
  schedule: { fontSize: 12, color: 'var(--t-546e7a)', marginTop: 2 },
  last: { fontSize: 13, fontWeight: 700 },
  lastDetail: { fontSize: 12, color: 'var(--t-78909c)', marginTop: 2 },
  switch: { fontSize: 12, fontWeight: 700, marginTop: 4 },
  steps: { margin: '10px 0 8px', paddingLeft: 18, fontSize: 13, color: 'var(--t-37474f)', lineHeight: 1.6 },
  meta: { fontSize: 12, color: 'var(--t-607d8b)', marginTop: 3 },
};
